import { useState, useEffect } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const adminHeaders = () => ({
    'Content-Type': 'application/json',
    'X-Admin-Password': password,
  });

  const handleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/participants`, { headers: adminHeaders() });
      if (res.status === 403) {
        setError('incorrect password');
        setLoading(false);
        return;
      }
      const data = await res.json();
      setParticipants(data);
      setAuthenticated(true);

      const surveyRes = await fetch(`${API}/admin/surveys`, { headers: adminHeaders() });
      const surveyData = await surveyRes.json();
      setSurveys(surveyData);
    } catch {
      setError('could not connect');
    }
    setLoading(false);
  };

  const handleExport = async () => {
    try {
      const res = await fetch(`${API}/admin/export-csv`, { headers: adminHeaders() });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'eunoia-beta-data.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Export failed');
    }
  };

  if (!authenticated) {
    return (
      <div className="velvet-void" style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '0 32px',
      }} data-testid="admin-login">
        <div style={{ width: '100%', maxWidth: '360px', textAlign: 'center' }} className="fade-in">
          <div className="breathing-sphere" style={{ margin: '0 auto 32px' }} />
          <h2 style={{
            fontFamily: "'Cormorant', Georgia, serif",
            fontStyle: 'italic', fontSize: '24px',
            color: 'rgba(255, 243, 225, 0.82)', marginBottom: '24px',
          }}>admin</h2>
          <input
            type="password"
            placeholder="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            className="velvet-input"
            data-testid="admin-password-input"
            style={{ marginBottom: '16px', textAlign: 'center' }}
          />
          {error && <p style={{ color: 'rgba(196, 137, 74, 0.8)', fontSize: '14px', marginBottom: '16px' }}>{error}</p>}
          <button onClick={handleLogin} disabled={loading} className="velvet-btn" data-testid="admin-login-btn">
            {loading ? 'checking...' : 'enter'}
          </button>
        </div>
      </div>
    );
  }

  const getSurveyForParticipant = (email) => surveys.find(s => s.user_email === email);

  return (
    <div className="velvet-void" style={{ minHeight: '100vh', padding: '32px' }} data-testid="admin-dashboard">
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <h1 style={{
            fontFamily: "'Cormorant', Georgia, serif",
            fontStyle: 'italic', fontSize: '28px',
            color: 'rgba(255, 243, 225, 0.82)',
          }}>Eunoia Beta</h1>
          <button onClick={handleExport} className="velvet-btn" data-testid="admin-export-btn"
            style={{ width: 'auto', padding: '10px 24px', fontSize: '14px' }}>
            Export CSV
          </button>
        </div>

        {/* Participants table */}
        <div style={{
          background: 'rgba(255, 243, 225, 0.03)',
          border: '1px solid rgba(255, 243, 225, 0.08)',
          borderRadius: '16px', overflow: 'hidden', marginBottom: '32px',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255, 243, 225, 0.08)' }}>
                {['Name', 'Code', 'Activated', 'Survey'].map(h => (
                  <th key={h} style={{
                    padding: '14px 16px', textAlign: 'left',
                    fontFamily: "'EB Garamond', Georgia, serif",
                    fontSize: '14px', color: 'rgba(255, 243, 225, 0.45)',
                    fontWeight: 400,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {participants.map((p) => {
                const survey = getSurveyForParticipant(p.email);
                return (
                  <tr key={p.id}
                    onClick={() => setSelectedParticipant(selectedParticipant === p.id ? null : p.id)}
                    style={{
                      borderBottom: '1px solid rgba(255, 243, 225, 0.04)',
                      cursor: 'pointer',
                      background: selectedParticipant === p.id ? 'rgba(196, 137, 74, 0.06)' : 'transparent',
                    }}
                    data-testid={`participant-row-${p.id}`}
                  >
                    <td style={{ padding: '12px 16px', fontFamily: "'EB Garamond', Georgia, serif", fontSize: '15px', color: 'rgba(255, 243, 225, 0.75)' }}>
                      {p.first_name} {p.last_name}
                    </td>
                    <td style={{ padding: '12px 16px', fontFamily: "monospace", fontSize: '13px', color: 'rgba(255, 243, 225, 0.50)', letterSpacing: '0.05em' }}>
                      {p.access_code || '\u2014'}
                    </td>
                    <td style={{ padding: '12px 16px', fontFamily: "'EB Garamond', Georgia, serif", fontSize: '14px', color: 'rgba(255, 243, 225, 0.45)' }}>
                      {p.nda_accepted_at ? new Date(p.nda_accepted_at).toLocaleDateString() : '\u2014'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        fontSize: '12px', padding: '4px 10px', borderRadius: '20px',
                        fontFamily: "'EB Garamond', Georgia, serif",
                        background: survey ? 'rgba(196, 137, 74, 0.12)' : 'rgba(255, 243, 225, 0.04)',
                        color: survey ? 'rgba(196, 137, 74, 0.85)' : 'rgba(255, 243, 225, 0.30)',
                      }}>{survey ? 'completed' : 'pending'}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Survey detail */}
        {selectedParticipant && (() => {
          const p = participants.find(x => x.id === selectedParticipant);
          const survey = p ? getSurveyForParticipant(p.email) : null;
          if (!survey) return (
            <div style={{
              background: 'rgba(255, 243, 225, 0.03)',
              border: '1px solid rgba(255, 243, 225, 0.08)',
              borderRadius: '16px', padding: '24px', textAlign: 'center',
            }}>
              <p style={{ fontFamily: "'EB Garamond', Georgia, serif", fontSize: '15px', color: 'rgba(255, 243, 225, 0.40)' }}>
                No survey response yet for {p.first_name} {p.last_name}
              </p>
            </div>
          );
          return (
            <div style={{
              background: 'rgba(255, 243, 225, 0.03)',
              border: '1px solid rgba(255, 243, 225, 0.08)',
              borderRadius: '16px', padding: '24px',
            }} data-testid="survey-detail">
              <h3 style={{
                fontFamily: "'Cormorant', Georgia, serif",
                fontStyle: 'italic', fontSize: '20px',
                color: 'rgba(255, 243, 225, 0.82)', marginBottom: '20px',
              }}>{p.first_name}'s Responses</h3>
              {Object.entries(survey.responses || {}).map(([key, value]) => (
                <div key={key} style={{
                  marginBottom: '16px',
                  paddingBottom: '16px',
                  borderBottom: '1px solid rgba(255, 243, 225, 0.04)',
                }}>
                  <p style={{
                    fontFamily: "'EB Garamond', Georgia, serif",
                    fontSize: '13px', color: 'rgba(255, 243, 225, 0.40)',
                    marginBottom: '4px',
                  }}>{key.replace(/_/g, ' ')}</p>
                  <p style={{
                    fontFamily: "'EB Garamond', Georgia, serif",
                    fontSize: '16px', color: 'rgba(255, 243, 225, 0.75)',
                  }}>{value}</p>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default AdminDashboard;
